import { Intent, NonIdealState } from "@blueprintjs/core";
import axios from "axios";
import { ShowHide } from "components/layout";
import { AlertConfirm, NotificationToaster } from "components/notifications";
import { EventEditModal, EventPanel } from "pages/asset/events";
import PropTypes from "prop-types";
import React, { useEffect, useState, useMemo } from "react";
import { CheckService, FleetService } from "services";
import moment from 'moment';

export function FleetDayDetailEventsTab(props) {
    const {
        assetDetails,
        eventsForPeriod,
        accountEventTypes,
        eventOptions,
        mappedAssetOptions,
        fetchingEvents,
        onEventsChanged
    } = props;

    const [events, setEvents] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [showReopenConfirm, setShowReopenConfirm] = useState(false);
    const [isEventEditModalOpen, setIsEventEditModalOpen] = useState(false);
    const [eventToModify, setEventToModify] = useState(null);

    useEffect(() => {
        if (eventsForPeriod) {
            if (eventsForPeriod.length === 0) {
                setEvents([]);
                setLoadingData(false);
            } else {
                getEvents();
            }
        }
    }, [eventsForPeriod])

    function resolveChecks(mappedEvents) {
        let eventChecks = mappedEvents?.filter(e => e.event?.checks && e.event.checks.length > 0);

        if (!eventChecks || eventChecks.length === 0) {
            return;
        }

        let allCheckIds = eventChecks.flatMap(e =>
            e.event.checks.map(c =>
                c.checkId
            )
        );

        //These should always be a unique list, but belt and braces
        allCheckIds = [...new Set(allCheckIds)];

        axios.all(allCheckIds.map(c =>
            CheckService.getCheck(c)
        ))
            .then(responses => {
                mappedEvents.map(e => {
                    let checkIds = e.event.checks.map(c => c.checkId);
                    e.detailedChecks = responses.filter(r => checkIds.includes(r.id));
                });
                setEvents(mappedEvents);
            })
            .catch(() => {
                //This just means the check details won't be available, we can still show the events without them so we'll just ignore this error
            });
    }

    function sortEvents(eventsToSort) {
        if (eventsToSort?.length < 1) {
            return [];
        }
        return eventsToSort.sort((a, b) => {
            let aEventDt = moment(a.eventDate).startOf('day');
            let bEventDt = moment(b.eventDate).startOf('day');
            if (!aEventDt.isSame(bEventDt)) {
                return aEventDt.isBefore(bEventDt) ? -1 : 1;
            }
            let aEventType = accountEventTypes.find(et => et.id === a.eventTypeId);
            let bEventType = accountEventTypes.find(et => et.id === b.eventTypeId);
            if (aEventType.priority !== bEventType.priority) {
                return aEventType.priority - bEventType.priority;
            }
            if (a.name !== b.name) {
                return (a.name || '').localeCompare(b.name || '');
            }
            return (a.statusName || '').localeCompare(b.statusName || '');
        });
    }

    function getEvents() {
        const tempEvents = eventsForPeriod || [];
        const eventIds = tempEvents.map(e => e.eventId);

        setLoadingData(true);

        if (eventIds.length === 0) {
            setLoadingData(false);
            return;
        }

        FleetService.detailedEventSearch(eventIds)
            .then((r) => {
                let sortedEvents = sortEvents(r);
                let mappedEvents = [];
                if (events && events.length > 0) {
                    mappedEvents = sortedEvents.map(e => {
                        let matchedEvent = events.find(ev => ev.event.eventId === e.eventId);
                        if (!matchedEvent && sortedEvents.indexOf(e) === 0) {
                            return {
                                expanded: true,
                                event: e
                            };
                        }
                        return {
                            expanded: matchedEvent?.expanded,
                            event: e
                        };
                    });
                } else {
                    mappedEvents = sortedEvents.map(e => {
                        return {
                            expanded: sortedEvents.indexOf(e) === 0,
                            event: e
                        };
                    });
                }
                //We will make the event show and then load the checks, we'll keep the checks loading in the background and then update the events with the check details as they come in.
                setEvents(mappedEvents);
                setLoadingData(false);
                resolveChecks(mappedEvents);
            })
            .finally(() => {
                setLoadingData(false);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, `Failed to load event data. Please try again.`);
            });
    }

    function onShowReopenConfirm(event) {
        setEventToModify(event);
        setShowReopenConfirm(true);
    }

    function confirmReopen() {
        FleetService.reopenEvent(eventToModify.eventId)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, `Event reopened successfully.`);
                // refresh event data
                refreshEvent();

                if (onEventsChanged) {
                    onEventsChanged();
                }
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, `Failed to reopen event. Please try again.`);
            })
            .finally(() => {
                setShowReopenConfirm(false);
                setEventToModify(null);
            });
    }

    function refreshEvent() {
        FleetService.getEvent(eventToModify.eventId)
            .then((getEventResponse) => {
                const tempEvents = events.map(ev =>
                    ev.event.eventId === getEventResponse.eventId
                        ? { ...ev, event: getEventResponse }
                        : ev
                );
                setEvents(tempEvents);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, `Failed to refresh event data. Please try again.`);
            });
    }

    function cancelReopen() {
        setShowReopenConfirm(false);
    }

    function handleDelete(id) {
        setLoadingData(true);

        FleetService.deleteEvent(id)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, "Event deleted successfully.");

                if (onEventsChanged) {
                    onEventsChanged();
                }
            })
            .catch((er) => {
                const VALIDATION_ERROR_STATUS = 400;
                if (er.response.status === VALIDATION_ERROR_STATUS) {
                    NotificationToaster.show(Intent.WARNING, er.response.data);
                    return;
                }

                NotificationToaster.show(Intent.DANGER, "Unable to delete the event, please try again.");
            })
            .finally(() => {
                setLoadingData(false);
            });
    }

    function onEventCollapseToggle(index) {
        const tempEvents = [...events];
        tempEvents[index].expanded = !tempEvents[index].expanded;
        setEvents(tempEvents);
    }

    const eventArea = useMemo(() => {
        return events.map((event, index) => { 
            const eventType = accountEventTypes.find(et => et.id === event.event.eventTypeId);

            return (
                <EventPanel
                    key={`event-panel-${event?.event.eventId}`}
                    event={event.event} 
                    eventType={eventType} 
                    eventOptions={eventOptions}
                    loadingData={fetchingEvents || loadingData} 
                    onDelete={handleDelete}
                    reopenFunction={() => onShowReopenConfirm(event.event)}
                    openEditEventModal={(e) => onEditEvent(event.event, e)}
                    detailedChecks={event.detailedChecks}
                    matchedEventTypeForAsset={eventType}
                    onEventCollapseToggle={() => onEventCollapseToggle(index)}
                    expanded={event.expanded} 
                />
            );
        });
    }, [events, loadingData, fetchingEvents]);

    function onEditEvent(event, e) {
        e.stopPropagation();
        setEventToModify(event);
        setIsEventEditModalOpen(true);   
    }

    function hideEventEditModal() {
        setIsEventEditModalOpen(false);
    }

    return ( 
        <>
            <ShowHide
                evaluator={fetchingEvents || loadingData || (events && events.length > 0)}
                show={eventArea}
                hide={<NonIdealState
                    title="No events available for this period"
                />}
            />

            <ShowHide
                evaluator={isEventEditModalOpen}
                show={(
                    <EventEditModal
                        isOpen={isEventEditModalOpen}
                        hideModal={hideEventEditModal}
                        onSuccess={() => {
                            if (onEventsChanged) {
                                onEventsChanged();
                            }
                        }}
                        eventId={eventToModify?.eventId}
                        eventTypeId={eventToModify?.eventTypeId}
                        asset={assetDetails}
                        eventOptions={eventOptions}
                        mappedAssetOptions={mappedAssetOptions}
                        loading={loadingData}
                    />
                )}
            />

            <AlertConfirm
                title="Reopen Event"
                isOpen={showReopenConfirm}
                onConfirm={confirmReopen}
                onCancel={cancelReopen}
                confirmButtonIntent="danger"
                confirmButtonText="Reopen"
            >
                <p>This event has been signed off, reopening this event will reset signoff. Are you sure you want to reopen this event?</p>
            </AlertConfirm>
        </>
    )
}

FleetDayDetailEventsTab.propTypes = {
    assetDetails: {},
    eventsForPeriod: [],
    accountEventTypes: [],
    eventOptions: {},
    fetchingEvents: false,
    onEventsChanged: () => { }
}

FleetDayDetailEventsTab.propTypes = {
    assetDetails: PropTypes.exact({
        assetId: PropTypes.number,
        assetLocation: PropTypes.exact({
            assetStatus: PropTypes.exact({ id: PropTypes.number }),
            assetOwnership: PropTypes.exact({ id: PropTypes.number }),
        }),
        registration: PropTypes.string
    }).isRequired,
    eventsForPeriod: PropTypes.array.isRequired,
    accountEventTypes: PropTypes.array,
    eventOptions: PropTypes.object,
    mappedAssetOptions: PropTypes.exact({
        ownershipAttributeMappings: PropTypes.array,
        assetStatusesMappings: PropTypes.array
    }),
    fetchingEvents: PropTypes.bool,
    onEventsChanged : PropTypes.func
}